import { Caption, Div, FormItem, FormLayoutGroup, Group, Input, SimpleCell, Tabs, TabsItem } from '@vkontakte/vkui';
import { useAppContext } from '../../../AppContext';
import { UserAvatar } from '../../components/UserAvatar';
import PropTypes from 'prop-types';
import './DistributionTypeTabsComponent.css';
import { useEffect, useState } from 'react';
import { prepareNumberValue } from '../../../helpers/helpers';
import * as Const from '../../Consts';

export const DISTRIBUTION_TYPE_EQUALLY = Const.DISTRIBUTION_TYPE_EQUALLY;
export const DISTRIBUTION_TYPE_VALUE = Const.DISTRIBUTION_TYPE_VALUE;
export const DISTRIBUTION_TYPE_PARTS = Const.DISTRIBUTION_TYPE_PARTS;

const amountToInt = (amount) => {
  return parseInt(amount * 1000);
}

const intToAmount = (int) => {
  return int / 1000;
}

export const calculateEquallyDistributions = (membersIds, amount, creatorId) => {
  if ((membersIds || []).length < 1 || !amount || !creatorId) {
    return {};
  }

  const price = amountToInt(amount);
  const totalCountMembers = (membersIds || []).length;
  const memberAmount = Math.trunc(price / totalCountMembers);
  const amountDiff = Math.trunc(price % totalCountMembers);
  const result = {};
  membersIds.map((id) => {
    result[id] = memberAmount;
  });

  if (amountDiff > 0) {
    if (result[creatorId]) {
      result[creatorId] += amountDiff;
    } else {
      result[membersIds[0]] += amountDiff;
    }
  }

  for (const key in result) {
    result[key] = intToAmount(result[key]);
  }

  return result;
}

export const calculatePartDistributions = (membersIds, amount, distributions, creatorId) => {
  const totalCountRations = (membersIds || []).reduce((prevValue, id) => {
    return prevValue + (!distributions ? 1 : Math.trunc(distributions[id] || 1));
  }, 0.0);

  const totalPrice = amountToInt(amount);
  const partAmount = Math.trunc(totalPrice / totalCountRations);
  const amountDiff = Math.trunc(totalPrice % totalCountRations);
  const result = {};
  membersIds.map((id) => {
    const userCountParts = (!distributions ? 1 : Math.trunc(distributions[id] || 1));
    result[id] = partAmount * userCountParts;
  });

  if (amountDiff > 0) {
    if (result[creatorId]) {
      result[creatorId] += amountDiff;
    } else {
      result[membersIds[0]] += amountDiff;
    }
  }

  for (const key in result) {
    result[key] = intToAmount(result[key]);
  }

  return result;
}

export const calculateValueDistributions = (membersIds, distributions) => {
  const result = {};
  (membersIds || []).forEach((memberId) => {
    result[memberId] = distributions[memberId] || 0;
  })

  return result;
}

export const valueFormatterEquallyDistributions = (membersIds) => {
  return membersIds;
}

export const valueFormatterPartDistributions = (membersIds, distributions) => {
  const result = {};
  membersIds.map((id) => {
    result[id] = Math.trunc(distributions[id] || 1);
  });

  return result;
}

export const valueFormatterValueDistributions = (membersIds, distributions) => {
  const result = {};
  membersIds.map((id) => {
    result[id] = distributions[id] || 0;
  });

  return result;
}

export const valueFormatterDistributions = (type, membersIds, partDistributions, valueDistributions) => {
  if (type === DISTRIBUTION_TYPE_EQUALLY) {
    return valueFormatterEquallyDistributions(membersIds);
  } else if (type === DISTRIBUTION_TYPE_PARTS) {
    return valueFormatterPartDistributions(membersIds, partDistributions);
  } else if (type === DISTRIBUTION_TYPE_VALUE) {
    return valueFormatterValueDistributions(membersIds, valueDistributions);
  }

  return null;
}

const AmountInput = ({amount, setAmount, min, max, setError, errorTextMin, errorTextMax, props}) => {
  return (<Input
    inputMode="decimal"
    value={amount}
    placeholder={'0.00'}
    pattern={'\\d+'}
    onKeyPress={(event) => {
      if (!['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ','].includes(event.key)) {
        event.preventDefault();
      }
    }}
    step={0.01}
    min={min}
    max={max}
    onChange={(event) => {
      if (event.target.value.length < 1) {
        setAmount('');
        return;
      }

      const value = prepareNumberValue(event.target.value.trim());
      if (value.length > 16) {
        return;
      }

      if (min && errorTextMin && value < min) {
        setError(errorTextMin);
      } else if (max && errorTextMax && Number(value) > max) {
        setError(errorTextMax);
        return;
      } else {
        setError('');
      }

      setAmount(value);
    }}
    onBlur={() => {
      const value = Number(prepareNumberValue(amount));
      if (isNaN(value)) {
        setError('');
        setAmount('');
      } else if (value < min) {
        setError(errorTextMin);
        setAmount('');
      } else if (value > max) {
        setError(errorTextMax);
      } else {
        setError('');
        setAmount(value.toFixed(2));
      }
    }}
    { ...props }
  />);
}

const EquallyType = ({totalPrice, creator, members, distributions, setDistribution}) => {
  const context = useAppContext();

  useEffect(() => {
    if ((members || []).length < 1 || totalPrice < 0.01 || !creator) {
      return;
    }

    const _amounts = calculateEquallyDistributions(members.map(({value}) => value), totalPrice, creator);
    setDistribution(_amounts);
  }, [totalPrice, creator, members]);

  if ((members || []).length < 1 || totalPrice < 0.01 || !creator) {
    return null;
  }

  return (<>{
    (members || []).map(({ img, label, value }) => {
      const amount = (distributions[value] || 0);
      return (<SimpleCell
        key={'equally_user_' + value}
        disabled
        before={<UserAvatar id={value} size={48} src={img} name={label} />}
        after={context.lang().formatPrice(amount)}
      >
        {label}
      </SimpleCell>);
    })
  }</>);
}

const PartType = ({totalPrice, creator, members, distributions, setDistribution, errors, setErrors}) => {
  const context = useAppContext();
  const [amounts, setAmounts] = useState({});

  useEffect(() => {
    setAmounts(calculatePartDistributions(members.map(({value}) => value), totalPrice, distributions, creator));
  }, [totalPrice, creator, members, distributions]);

  const AmountComponent = ({amount}) => {
    return (<Caption
      className={'PartType__amountBlock'}
      level="3"
    >
      {context.lang().formatPrice(amount)}
    </Caption>);
  }

  return (<>{
    members.map(({ img, label, value }) => {
      const error = errors[value] || '';
      const amount = amounts[value];

      return (<FormItem
        key={'part_user_' + value}
        status={error ? 'error' : ''}
        bottom={(<div className={'ValueType__error'}>{error}</div>)}
      >
        <SimpleCell
          before={<UserAvatar id={value} size={48} src={img} name={label} />}
          after={<Input
            className={'PartType__input'}
            inputMode="decimal"
            onKeyPress={(event) => {
              if (!['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(event.key)) {
                event.preventDefault();
              }
            }}
            value={distributions[value] || ''}
            pattern={'\\d+'}
            step={1}
            min={1}
            max={100}
            placeholder={'1'}
            onChange={(event) => {
              const v = event.target.value|0;
              if (v < 1) {
                return setDistribution({ ...distributions, [value]: undefined});
              } else if (v > 100) {
                return setDistribution({ ...distributions, [value]: 100});
              }

              return setDistribution({ ...distributions, [value]: v});
            }}
          />}
          subtitle={<AmountComponent amount={amount}/>}
        >
          {label}
        </SimpleCell>
      </FormItem>);
    })
  }</>);
}

const ValueType = ({totalPrice, members, distributions, errors, setErrors, setDistribution, errorExcessTotalAmount, errorMinAmount}) => {
  const context = useAppContext();

  const membersSumAmount = (members || []).reduce((prevValue, {value}) => prevValue + parseFloat(distributions[value] || 0), 0.0);
  const errorTotalAmount = membersSumAmount > totalPrice;
  const errorTextTotalAmount = errorExcessTotalAmount || context.lang().getKey('errors', 'excess_total_amount');

  return (<FormLayoutGroup mode="vertical" className="DistributionTypeValue">
    {
      (members || []).map(({ img, label, value }) => {
        const error = errorTotalAmount ? errorTextTotalAmount : (errors[value] || '');

        return (<FormItem
          key={'value_user_' + value}
          status={error ? 'error' : ''}
          bottom={(<div className={'ValueType__error'}>{error}</div>)}
        >
          <SimpleCell
            before={<UserAvatar id={value} size={48} src={img} name={label} />}
            after={<AmountInput
              amount={distributions[value] || ''}
              setAmount={(amount) => setDistribution({ ...distributions, [value]: amount })}
              min={0}
              max={Number(totalPrice || 0)}
              setError={(text) => setErrors({ ...errors, [value]: text })}
              errorTextMin={context.lang().getKey('errors', 'amount_error_min')}
              errorTextMax={context.lang().getKey('errors', 'amount_error_max')}
            />}
          >
            {label}
          </SimpleCell>
        </FormItem>);
      })
    }
  </FormLayoutGroup>);
}

export const DistributionTypeTabsComponent = ({
  price,
  creator,
  members,
  distributionType, setDistributionType,
  equallyDistributionsValue, setEquallyDistributionsValue,
  partDistributionsValue, setPartDistributionsValue, partDistributionsErrors, setPartDistributionsErrors,
  valueDistributionsValue, setValueDistributionsValue, valueDistributionsErrors, setValueDistributionsErrors,
  labelTabEqually,
  labelTabValue,
  labelTabParts,
  placeholder,
}) => {
  return (<>
    <FormItem
      top={'Тип расчета'}
    >
    <Tabs
      className={'DistributionTabs'}
      mode={'secondary'}
    >
      <TabsItem
        selected={distributionType === DISTRIBUTION_TYPE_EQUALLY}
        onClick={() => setDistributionType(DISTRIBUTION_TYPE_EQUALLY)}
      >
        {labelTabEqually}
      </TabsItem>
      <TabsItem
        selected={distributionType === DISTRIBUTION_TYPE_PARTS}
        onClick={() => setDistributionType(DISTRIBUTION_TYPE_PARTS)}
        disabled={price < 0.01 || members.length < 1}
      >
        {labelTabParts}
      </TabsItem>
      <TabsItem
        selected={distributionType === DISTRIBUTION_TYPE_VALUE}
        onClick={() => setDistributionType(DISTRIBUTION_TYPE_VALUE)}
        disabled={price < 0.01 || members.length < 1}
      >
        {labelTabValue}
      </TabsItem>
    </Tabs>
    <Group>
      {
        (members.length < 1 || price < 0.01) ? (<Div className={'DistributionTypeTabs__placeholder'}><Caption level={'3'}>{placeholder}</Caption></Div>) : undefined
      }
      {
        distributionType === DISTRIBUTION_TYPE_EQUALLY ? (<EquallyType
          creator={creator}
          totalPrice={price}
          members={members}
          distributions={equallyDistributionsValue}
          setDistribution={setEquallyDistributionsValue}
        />) : undefined
      }
      {
        distributionType === DISTRIBUTION_TYPE_PARTS ? (<PartType
          creator={creator}
          totalPrice={price}
          members={members}
          distributions={partDistributionsValue}
          setDistribution={setPartDistributionsValue}
          errors={partDistributionsErrors}
          setErrors={setPartDistributionsErrors}
        />) : undefined
      }
      {
        distributionType === DISTRIBUTION_TYPE_VALUE ? (<ValueType
          totalPrice={price}
          members={members}
          distributions={valueDistributionsValue}
          setDistribution={setValueDistributionsValue}
          errors={valueDistributionsErrors}
          setErrors={setValueDistributionsErrors}
        />) : undefined
      }
    </Group>
    </FormItem>
  </>);
}

const members = PropTypes.shape({
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  label: PropTypes.string.isRequired,
  img: PropTypes.string
});

EquallyType.protoType = {
  totalPrice: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(members.isRequired).isRequired,
  distributions: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.number.isRequired,
  }),
  setDistribution: PropTypes.func.isRequired,
};

PartType.protoType = {
  totalPrice: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(members.isRequired).isRequired,
  distributions: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.number.isRequired,
  }),
  setDistribution: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.string.isRequired,
  }),
}

ValueType.protoType = {
  totalPrice: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(members.isRequired).isRequired,
  distributions: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.number.isRequired,
  }),
  setDistribution: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.string.isRequired,
  }),
  errorExcessTotalAmount: PropTypes.string,
}

DistributionTypeTabsComponent.protoType = {
  price: PropTypes.number.isRequired,
  creator: PropTypes.number.isRequired,
  members: PropTypes.arrayOf(members.isRequired).isRequired,
  distributionType: PropTypes.string.isRequired,
  setDistributionType: PropTypes.func.isRequired,
  equallyDistributionsValue: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.number.isRequired,
  }),
  setEquallyDistributionsValue: PropTypes.func.isRequired,
  partDistributionsValue: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.number.isRequired,
  }),
  setPartDistributionsValue: PropTypes.func.isRequired,
  partDistributionsErrors: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.string.isRequired,
  }),
  valueDistributionsValue: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.number.isRequired,
  }),
  setValueDistributionsValue: PropTypes.func.isRequired,
  valueDistributionsErrors: PropTypes.shape({
    [PropTypes.number.isRequired]: PropTypes.string.isRequired,
  }),
  labelTabEqually: PropTypes.string.isRequired,
  labelTabValue: PropTypes.string.isRequired,
  labelTabParts: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
}
